import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Leistungen', href: '#services' },
    { label: 'Über uns', href: '#about' },
    { label: 'Regionen', href: '/regionen', isRoute: true },
    { label: 'Neuigkeiten', href: '#blog' },
    { label: 'Novatus Care', href: process.env.REACT_APP_CARE_URL || 'https://heatpump-care.emergent.host', isExternal: true }
  ];

  const scrollToSection = (e, href, isRoute, isExternal) => {
    e.preventDefault();
    
    // Wenn es ein externer Link ist, öffne in neuem Tab
    if (isExternal) {
      window.open(href, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      return;
    }
    
    // Wenn es eine Route ist (z.B. /regionen), navigiere direkt
    if (isRoute) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    }
  };

  const handleWaermepumpeClick = () => {
    navigate('/waermepumpe');
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1A3A2E]/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-8">
          {/* Logo - links */}
          <a href="/" onClick={handleLogoClick} className="flex items-center space-x-2 cursor-pointer flex-shrink-0">
            <div className="bg-white rounded-lg p-2 md:p-3">
              <img 
                src="/novatus-logo.png" 
                alt="Novatus Energie" 
                className="h-10 md:h-20 w-auto"
              />
            </div>
          </a>

          {/* Zentrierte Navigation - flexible Mitte */}
          <nav className="hidden md:flex items-center justify-center flex-1">
            <div className="flex items-center gap-16">
              {menuItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href, item.isRoute, item.isExternal)}
                  className="text-white hover:text-[#E8F5E0] transition-colors duration-200 text-lg font-medium whitespace-nowrap"
                  title={item.isExternal ? "Service & Wartung für Ihre Wärmepumpe" : ""}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>

          {/* Button und Telefon rechts - zentriert im Frame */}
          <div className="hidden md:flex flex-shrink-0 flex-col items-center gap-3">
            <Button
              onClick={handleWaermepumpeClick}
              className="bg-[#E8F5E0] text-[#1A3A2E] hover:bg-[#d4e8ca] transition-all duration-300 whitespace-nowrap text-lg font-semibold px-8 py-6"
            >
              Wärmepumpe anfragen
            </Button>
            
            {/* Telefon-Hotline unter dem Button */}
            <a 
              href="tel:+493039820570" 
              className="flex items-center gap-2 bg-[#E8F5E0]/20 hover:bg-[#E8F5E0]/30 px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Phone className="w-5 h-5 text-[#E8F5E0]" />
              <span className="text-base font-semibold text-white whitespace-nowrap">030 398 205 700</span>
            </a>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 px-4 -mx-6 bg-[#1A3A2E] flex flex-col space-y-4">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href, item.isRoute, item.isExternal)}
                className="text-white hover:text-[#E8F5E0] transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
            <Button
              onClick={handleWaermepumpeClick}
              className="bg-[#E8F5E0] text-[#1A3A2E] hover:bg-[#d4e8ca] w-full"
            >
              Wärmepumpe anfragen
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;